import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LedgerStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../../components/common/ThemedText';
import * as ledgerApi from '../../lib/api/ledger';
import * as usersApi from '../../lib/api/users';
import { storage } from '../../utils/storage';
import { showSuccessToast, showErrorToast, Alert } from '../../utils/toast';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';

type CreateLedgerAccountNavigationProp = StackNavigationProp<LedgerStackParamList, 'CreateLedgerAccount'>;
type CreateLedgerAccountRouteProp = RouteProp<LedgerStackParamList, 'CreateLedgerAccount'>;

const LEDGER_TYPES = ['Business', 'Personal', 'Academics', 'Social'];

const CreateLedgerAccountScreen: React.FC = () => {
  const navigation = useNavigation<CreateLedgerAccountNavigationProp>();
  const route = useRoute<CreateLedgerAccountRouteProp>();
  const { theme } = useTheme();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { keyboardHeight } = useKeyboardHeight();
  
  // Check if we're in edit mode
  const editMode = route.params?.editMode || false;
  const ledgerId = route.params?.ledgerId;
  const existingData = route.params?.existingData;
  
  const [personName, setPersonName] = useState(existingData?.personName || '');
  const [selectedType, setSelectedType] = useState(existingData?.type || 'Personal');
  const [note, setNote] = useState(existingData?.note || '');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Share with users (multiple people)
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (showUserSearch) {
      loadUsers(1, true);
    }
  }, [userSearch, showUserSearch]);

  const loadUsers = async (page: number, isNewSearch: boolean = false) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      setLoadingUsers(true);
      const result = await usersApi.searchUsers(token, userSearch, page, 10);
      
      if (isNewSearch) {
        setUsers(result.users);
      } else {
        setUsers(prev => [...prev, ...result.users]);
      }
      
      setHasMoreUsers(result.hasMore);
      setUserPage(page);
    } catch (error: any) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const selectUser = (user: any) => {
    const isAlreadySelected = selectedUsers.some(u => u.id === user.id);
    if (isAlreadySelected) {
      setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers(prev => [...prev, user]);
    }
  };

  const handleSave = async () => {
    const token = await storage.getToken();
    if (!token) {
      Alert.alert('Error', 'Please log in to continue');
      return;
    }

    // Validation
    if (!personName.trim()) {
      Alert.alert('Error', 'Person/Business name is required');
      return;
    }

    try {
      setSaving(true);

      if (editMode && ledgerId) {
        // Update existing ledger
        await ledgerApi.updateLedger(token, ledgerId, {
          personName: personName.trim(),
          type: selectedType,
          note: note.trim() || undefined,
        });
        showSuccessToast('Ledger updated successfully!');
      } else {
        // Create new ledger
        await ledgerApi.createLedger(token, {
          personName: personName.trim(),
          isBookmarked,
          type: selectedType,
          note: note.trim() || undefined,
          sharedUserIds: selectedUsers.map(u => u.id),
        });
        showSuccessToast('Ledger account created successfully!');
      }
      
      navigation.goBack();
    } catch (error: any) {
      console.error('Error saving ledger:', error);
      showErrorToast(error.message || `Failed to ${editMode ? 'update' : 'create'} ledger account. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View
        style={[styles.header, { borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {editMode ? 'Edit Ledger' : 'New Ledger Account'}
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
          <View style={styles.form}>
            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Person / Business Name *</ThemedText>
              <TextInput
                style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
                placeholder="e.g., Rajesh Kumar or ABC Suppliers"
                value={personName}
                onChangeText={setPersonName}
                placeholderTextColor={theme.textSecondary}
                autoFocus
              />
              <ThemedText style={[styles.helperText, { color: theme.textTertiary }]}>
                Create a ledger to track all transactions with this person or business
              </ThemedText>
            </View>

            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Type *</ThemedText>
              <View style={styles.typeGrid}>
                {LEDGER_TYPES.map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={[
                      styles.typeButton,
                      { backgroundColor: theme.backgroundSecondary, borderColor: theme.border },
                      selectedType === type && { backgroundColor: theme.accent + '20', borderColor: theme.accent },
                    ]}
                    onPress={() => setSelectedType(type)}
                    activeOpacity={0.7}
                  >
                    <ThemedText style={[
                      styles.typeText,
                      { color: theme.text },
                      selectedType === type && { color: theme.accent, fontWeight: '600' },
                    ]}>
                      {type}
                    </ThemedText>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Note (Optional)</ThemedText>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
                placeholder="Add any notes about this ledger"
                value={note}
                onChangeText={setNote}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                placeholderTextColor={theme.textSecondary}
              />
            </View>

            {/* Only show share option in create mode, not edit mode */}
            {!editMode && (
              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Share With (Optional)</ThemedText>
                <TouchableOpacity
                  style={[styles.shareButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                  onPress={() => setShowUserSearch(!showUserSearch)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="person-outline" size={20} color={theme.text} />
                  <ThemedText style={[styles.shareButtonText, { color: theme.text }]}>
                    {selectedUsers.length > 0 
                      ? `${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} selected` 
                      : 'Select users to share'}
                  </ThemedText>
                  <Ionicons name={showUserSearch ? 'chevron-up' : 'chevron-down'} size={20} color={theme.textSecondary} />
                </TouchableOpacity>

              {selectedUsers.length > 0 && (
                <View style={styles.selectedUsers}>
                  {selectedUsers.map(user => (
                    <View key={user.id} style={[styles.selectedUserChip, { backgroundColor: theme.accent + '20' }]}>
                      <ThemedText style={[styles.selectedUserName, { color: theme.accent }]}>
                        {user.name}
                      </ThemedText>
                      <TouchableOpacity onPress={() => selectUser(user)}>
                        <Ionicons name="close-circle" size={18} color={theme.accent} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              {showUserSearch && (
                <View style={[styles.userSearchContainer, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
                  <View style={styles.searchInputContainer}>
                    <Ionicons name="search" size={20} color={theme.textSecondary} />
                    <TextInput
                      style={[styles.searchInput, { color: theme.text }]}
                      placeholder="Search by name, phone, or profession"
                      value={userSearch}
                      onChangeText={setUserSearch}
                      placeholderTextColor={theme.textSecondary}
                    />
                  </View>

                  <ScrollView style={styles.userList} nestedScrollEnabled={true}>
                    {loadingUsers && users.length === 0 ? (
                      <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" color={theme.accent} />
                      </View>
                    ) : users.length === 0 ? (
                      <View style={styles.emptyContainer}>
                        <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
                          No users found
                        </ThemedText>
                      </View>
                    ) : (
                      users.map((item) => {
                        const isSelected = selectedUsers.some(u => u.id === item.id);
                        return (
                          <TouchableOpacity
                            key={item.id}
                            style={[styles.userItem, { borderBottomColor: theme.border }]}
                            onPress={() => selectUser(item)}
                            activeOpacity={0.7}
                          >
                            <View style={styles.userInfo}>
                              <ThemedText style={[styles.userName, { color: theme.text }]}>
                                {item.name}
                              </ThemedText>
                              <ThemedText style={[styles.userDetails, { color: theme.textSecondary }]}>
                                {item.profession} • {item.city}
                              </ThemedText>
                            </View>
                            <View style={[
                              styles.checkbox, 
                              { borderColor: isSelected ? theme.accent : theme.border },
                              isSelected && { backgroundColor: theme.accent }
                            ]}>
                              {isSelected && (
                                <Ionicons name="checkmark" size={18} color="#FFFFFF" />
                              )}
                            </View>
                          </TouchableOpacity>
                        );
                      })
                    )}
                    {hasMoreUsers && (
                      <TouchableOpacity
                        style={styles.loadMoreButton}
                        onPress={() => loadUsers(userPage + 1)}
                        disabled={loadingUsers}
                      >
                        {loadingUsers ? (
                          <ActivityIndicator size="small" color={theme.accent} />
                        ) : (
                          <ThemedText style={[styles.loadMoreText, { color: theme.accent }]}>
                            Load More
                          </ThemedText>
                        )}
                      </TouchableOpacity>
                    )}
                  </ScrollView>
                </View>
              )}
              </View>
            )}

            <TouchableOpacity
              style={styles.bookmarkContainer}
              onPress={() => setIsBookmarked(!isBookmarked)}
              activeOpacity={0.7}
            >
              <View style={[styles.checkbox, { borderColor: theme.border }]}>
                {isBookmarked && (
                  <Ionicons name="checkmark" size={18} color={theme.accent} />
                )}
              </View>
              <View style={styles.bookmarkTextContainer}>
                <ThemedText style={[styles.bookmarkLabel, { color: theme.text }]}>
                  Bookmark this ledger
                </ThemedText>
                <ThemedText style={[styles.bookmarkHelper, { color: theme.textTertiary }]}>
                  Quick access from bookmarked filter
                </ThemedText>
              </View>
            </TouchableOpacity>
            
            {/* Action Buttons */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel}>
                <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.saveButton, { backgroundColor: theme.accent }]} 
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator color={theme.textInverse} />
                ) : (
                  <ThemedText style={[styles.saveButtonText, { color: theme.textInverse }]}>
                    {editMode ? 'Save Changes' : 'Create Ledger'}
                  </ThemedText>
                )}
              </TouchableOpacity>
            </View>

            <FormFooterSpacer
              headerHeight={headerHeight}
              bottomInset={bottomInset}
              keyboardHeight={keyboardHeight}
            />
          </View>
        </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  form: {
    padding: 16,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  textArea: {
    minHeight: 80,
    paddingTop: 12,
  },
  helperText: {
    fontSize: 12,
    marginTop: 6,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  typeButton: {
    flex: 1,
    minWidth: '47%',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
  },
  typeText: {
    fontSize: 14,
    fontWeight: '500',
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  shareButtonText: {
    flex: 1,
    fontSize: 15,
  },
  selectedUsers: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  selectedUserChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 6,
  },
  selectedUserName: {
    fontSize: 13,
    fontWeight: '500',
  },
  userSearchContainer: {
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    maxHeight: 300,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    paddingVertical: 4,
  },
  userList: {
    maxHeight: 240,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 2,
  },
  userDetails: {
    fontSize: 13,
  },
  loadingContainer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyContainer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
  },
  loadMoreButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
  bookmarkContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  bookmarkTextContainer: {
    flex: 1,
  },
  bookmarkLabel: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 2,
  },
  bookmarkHelper: {
    fontSize: 12,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CreateLedgerAccountScreen;
